<template>
  <div class="admin-notice">
    <!-- 페이지 헤더 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">공지사항</h1>
        <p class="page-subtitle">공지사항을 관리합니다</p>
      </div>
      <div class="header-actions">
        <v-btn color="white" variant="flat" @click="goToWrite">
          <v-icon start>mdi-plus</v-icon>
          공지 작성
        </v-btn>
      </div>
    </div>

    <!-- 검색 및 필터 -->
    <v-card class="search-card" elevation="1">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-select
              v-model="filterType"
              :items="typeOptions"
              item-title="text"
              item-value="value"
              label="분류"
              variant="outlined"
              density="compact"
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              label="검색"
              placeholder="제목, 작성자 검색"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2">
            <v-btn 
              color="primary" 
              variant="tonal"
              @click="loadNotices"
              :loading="loading"
            >
              <v-icon start>mdi-refresh</v-icon>
              새로고침
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 공지사항 목록 -->
    <v-card class="notice-list-card" elevation="1">
      <v-data-table
        :headers="headers"
        :items="filteredNotices"
        :loading="loading"
        :items-per-page="-1"
        class="notice-table"
        hover
        @click:row="goToDetail"
      >
        <!-- 번호 -->
        <template v-slot:item.nc_idx="{ item, index }">
          <span class="notice-idx">{{ pagination.total - ((pagination.page - 1) * pagination.limit) - index }}</span>
        </template>

        <!-- 분류 -->
        <template v-slot:item.nc_type="{ item }">
          <div class="type-chips">
            <v-chip 
              :color="getCategoryColor(item.nc_category)" 
              size="small"
              variant="flat"
              class="mr-1"
            >
              {{ getCategoryText(item.nc_category) }}
            </v-chip>
            <v-chip 
              :color="item.nc_type === 'ADMIN' ? 'grey-darken-1' : 'teal'" 
              size="x-small"
              variant="outlined"
            >
              {{ item.nc_type === 'ADMIN' ? '관리자' : item.nc_sisul_name || '센터' }}
            </v-chip>
          </div>
        </template>

        <!-- 제목 -->
        <template v-slot:item.nc_title="{ item }">
          <div class="notice-title-cell">
            <span class="notice-title">{{ item.nc_title }}</span>
            <v-icon v-if="item.file_count > 0" size="small" color="grey" class="ml-1">
              mdi-attachment
            </v-icon>
            <span v-if="item.file_count > 0" class="file-count">({{ item.file_count }})</span>
            <v-chip v-if="isNew(item.nc_wdate)" color="error" size="x-small" class="ml-2">NEW</v-chip>
          </div>
        </template>

        <!-- 작성자 -->
        <template v-slot:item.nc_writer_name="{ item }">
          <div class="writer-info">
            <span>{{ item.nc_type === 'ADMIN' ? '관리자' : item.nc_sisul_name || '-' }}</span>
          </div>
        </template>

        <!-- 조회수 -->
        <template v-slot:item.nc_hit="{ item }">
          <span class="hit-count">{{ item.nc_hit }}</span>
        </template>

        <!-- 작성일 -->
        <template v-slot:item.nc_wdate="{ item }">
          {{ formatDate(item.nc_wdate) }}
        </template>

        <!-- 로딩 -->
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
        </template>

        <!-- 데이터 없음 -->
        <template v-slot:no-data>
          <div class="no-data-message">
            <v-icon size="64" color="grey-lighten-1">mdi-bulletin-board</v-icon>
            <p>등록된 공지사항이 없습니다</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- 스낵바 -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      {{ snackbar.text }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">닫기</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';

interface Notice {
  nc_idx: number;
  nc_type: 'ADMIN' | 'CENTER';
  nc_category: 'NOTICE' | 'UPDATE' | 'EVENT' | 'URGENT';
  nc_sisul_code: string | null;
  nc_sisul_name: string | null;
  nc_writer_name: string;
  nc_title: string;
  nc_hit: number;
  nc_is_active: string;
  nc_wdate: string;
  nc_rdate: string | null;
  file_count: number;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const router = useRouter();
const store = useStore();

// 상태
const loading = ref(false);
const notices = ref<Notice[]>([]);
const pagination = ref<Pagination>({
  total: 0,
  page: 1,
  limit: 15,
  totalPages: 0
});
const searchQuery = ref('');
const filterType = ref('ALL');
const snackbar = ref({
  show: false,
  text: '',
  color: 'info'
});

// 타입 필터 옵션
const typeOptions = [
  { text: '전체', value: 'ALL' },
  { text: '관리자', value: 'ADMIN' },
  { text: '센터장', value: 'CENTER' }
];

// 테이블 헤더
const headers = [
  { title: '번호', key: 'nc_idx', sortable: false, width: '70px', align: 'center' },
  { title: '분류', key: 'nc_type', sortable: false, width: '180px' },
  { title: '제목', key: 'nc_title', sortable: false },
  { title: '작성자', key: 'nc_writer_name', sortable: false, width: '100px' },
  { title: '조회', key: 'nc_hit', sortable: true, width: '90px', align: 'center' },
  { title: '작성일', key: 'nc_wdate', sortable: true, width: '140px', align: 'center' }
];

// 필터링된 공지사항
const filteredNotices = computed(() => {
  let result = notices.value;
  
  // 타입 필터
  if (filterType.value !== 'ALL') {
    result = result.filter(n => n.nc_type === filterType.value);
  }
  
  // 검색어 필터
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(n => 
      n.nc_title?.toLowerCase().includes(query) ||
      n.nc_writer_name?.toLowerCase().includes(query)
    );
  }
  
  return result;
});

// 공지사항 목록 로드
const loadNotices = async (page = 1) => {
  loading.value = true;
  try {
    const response = await axios.get('/api/notice/list', {
      params: { page, limit: 15 },
      withCredentials: true
    });
    
    if (response.data.success) {
      notices.value = response.data.notices;
      pagination.value = response.data.pagination;
    } else {
      showSnackbar('공지사항을 불러오는데 실패했습니다.', 'error');
    }
  } catch (error: any) {
    console.error('공지사항 목록 로드 오류:', error);
    showSnackbar('서버 오류가 발생했습니다.', 'error');
  } finally {
    loading.value = false;
  }
};

// 상세 페이지로 이동
const goToDetail = (event: Event, { item }: { item: Notice }) => {
  router.push(`/admin/notice/${item.nc_idx}`);
};

// 작성 페이지로 이동
const goToWrite = () => {
  router.push('/admin/notice/write');
};

// 유틸리티 함수
const formatDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  
  return `${year}.${month}.${day} ${hours}:${minutes}`;
};

const isNew = (date: string) => {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  return diff < 3 * 24 * 60 * 60 * 1000; // 3일 이내
};

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'NOTICE': return 'primary';
    case 'UPDATE': return 'info';
    case 'EVENT': return 'success';
    case 'URGENT': return 'error';
    default: return 'grey';
  }
};

const getCategoryText = (category: string) => {
  switch (category) {
    case 'NOTICE': return '공지';
    case 'UPDATE': return '업데이트';
    case 'EVENT': return '이벤트';
    case 'URGENT': return '긴급';
    default: return category;
  }
};

const showSnackbar = (text: string, color: string = 'info') => {
  snackbar.value = { show: true, text, color };
};

onMounted(() => {
  loadNotices();
});
</script>

<style lang="scss" scoped>
@import '../../assets/styles/fonts.scss';

.admin-notice {
  font-family: $Noto_Sans_KR;
  padding: 0 16px 16px 16px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
  border-radius: 12px;
  color: white;
  
  .page-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0;
  }
  
  .page-subtitle {
    font-size: 14px;
    opacity: 0.8;
    margin: 4px 0 0 0;
  }
}

.search-card {
  margin-bottom: 16px;
  border-radius: 12px;
}

.notice-list-card {
  border-radius: 12px;
  overflow: hidden;
}

.notice-table {
  cursor: pointer;
  
  .notice-idx {
    color: #666;
    font-size: 13px;
  }
  
  .type-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .notice-title-cell {
    display: flex;
    align-items: center;
    
    .notice-title {
      font-weight: 500;
      color: #333;
    }
    
    .file-count {
      font-size: 12px;
      color: #999;
      margin-left: 2px;
    }
  }
  
  .writer-info {
    .sisul-name {
      font-size: 12px;
      color: #999;
      margin-left: 4px;
    }
  }
  
  .hit-count {
    color: #666;
    font-size: 13px;
  }
}

.no-data-message {
  padding: 60px 20px;
  text-align: center;
  color: #999;
  
  p {
    margin-top: 16px;
    font-size: 16px;
  }
}
</style>

